/**
 * Colours and motion of the shared Button and ToggleSwitch (#1127).
 *
 * Every colour is a theme variable, so light, dark and custom themes all
 * apply. Text on a filled control uses `--theme-*-foreground`, which
 * SettingsContext picks from the theme's text/background colour by contrast
 * (see `lib/theme-fill.ts`). Motion follows the brand: a calm mechanical ease,
 * a 1px press nudge, no scaling, and nothing moves under reduced motion.
 * The focus ring is in `styles/focus.css` (`data-slot`), because the global
 * unlayered `button:focus-visible` rule there beats any utility class.
 */

/** The design system's standard ease and a duration inside its 120–280ms band. */
export const CONTROL_MOTION =
  'duration-150 ease-[cubic-bezier(.2,0,.2,1)] motion-reduce:transition-none'

export const BUTTON_BASE = [
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[6px] text-sm font-medium shrink-0',
  'transition-[background-color,border-color,color,box-shadow,translate]',
  CONTROL_MOTION,
  'active:translate-y-px motion-reduce:active:translate-y-0',
  'disabled:pointer-events-none disabled:opacity-50',
  "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 [&_svg]:shrink-0",
  'aria-invalid:border-[var(--cdx-color-danger)] aria-invalid:ring-[3px] aria-invalid:ring-[color-mix(in_srgb,var(--cdx-color-danger)_25%,transparent)]',
].join(' ')

export const BUTTON_VARIANTS = {
  default:
    'bg-[var(--theme-interactive-color)] text-[var(--theme-interactive-foreground)] shadow-xs hover:bg-[var(--theme-interactive-hover)] active:bg-[var(--theme-interactive-active)]',
  destructive:
    'bg-[var(--cdx-color-danger)] text-[var(--theme-danger-foreground)] shadow-xs hover:bg-[var(--theme-danger-hover)] active:bg-[var(--theme-danger-active)]',
  outline:
    'border border-[var(--window-border-color)] bg-[var(--window-background)] text-[var(--text-color)] shadow-xs hover:bg-[var(--sidebar-hover)] active:bg-[var(--menu-item-hover)]',
  secondary:
    'border border-[var(--window-border-color)] bg-[var(--window-background)] text-[var(--text-color)] shadow-xs hover:bg-[var(--sidebar-hover)] active:bg-[var(--menu-item-hover)]',
  ghost:
    'text-[var(--text-color)] hover:bg-[var(--sidebar-hover)] active:bg-[var(--menu-item-hover)]',
  // The accent as text, pulled toward the text colour where it would not read.
  link: 'text-[var(--theme-interactive-text)] underline-offset-4 hover:underline',
} as const

export const TOGGLE_TRACK = [
  'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border',
  'transition-colors',
  CONTROL_MOTION,
].join(' ')

export const TOGGLE_TRACK_ON =
  'border-[var(--theme-interactive-color)] bg-[var(--theme-interactive-color)] hover:border-[var(--theme-interactive-hover)] hover:bg-[var(--theme-interactive-hover)]'

export const TOGGLE_TRACK_OFF =
  'border-[var(--window-border-color)] bg-[color-mix(in_srgb,var(--text-color)_12%,var(--window-background))] hover:bg-[color-mix(in_srgb,var(--text-color)_20%,var(--window-background))]'

export const TOGGLE_THUMB = [
  'pointer-events-none inline-block h-4 w-4 rounded-full shadow-sm',
  'transition-[translate,background-color]',
  CONTROL_MOTION,
].join(' ')

/** Light where it shows on the accent, otherwise the track's foreground (see `lib/theme-fill.ts`). */
export const TOGGLE_THUMB_ON = 'translate-x-[23px] bg-[var(--theme-interactive-knob)]'

export const TOGGLE_THUMB_OFF = 'translate-x-[3px] bg-[var(--text-secondary)]'

/**
 * Slider (#1135). The track is the off-state toggle track, the filled part
 * and the thumb ring are the accent, the thumb itself the window colour.
 */
export const SLIDER_TRACK =
  'relative h-1.5 w-full grow overflow-hidden rounded-full bg-[color-mix(in_srgb,var(--text-color)_14%,var(--window-background))]'

export const SLIDER_RANGE = 'absolute h-full bg-[var(--theme-interactive-color)]'

export const SLIDER_THUMB = [
  'block h-4 w-4 rounded-full border-2 border-[var(--theme-interactive-color)] bg-[var(--window-background)] shadow-sm',
  'transition-[background-color,border-color,box-shadow]',
  CONTROL_MOTION,
  'hover:bg-[color-mix(in_srgb,var(--theme-interactive-color)_14%,var(--window-background))]',
  'disabled:pointer-events-none data-[disabled]:opacity-50',
].join(' ')

/**
 * Slider on top of a video (`variant="overlay"`): the player chrome is light
 * on a dark scrim in every theme, so it uses the theme's fixed light neutral.
 */
export const SLIDER_TRACK_OVERLAY =
  'relative h-1 w-full grow overflow-hidden rounded-full bg-[color-mix(in_srgb,var(--theme-neutral-50)_35%,transparent)]'

export const SLIDER_RANGE_OVERLAY = 'absolute h-full bg-[var(--theme-neutral-50)]'

export const SLIDER_THUMB_OVERLAY = [
  'block h-3 w-3 rounded-full bg-[var(--theme-neutral-50)] shadow-sm',
  CONTROL_MOTION,
  'data-[disabled]:opacity-50',
].join(' ')

/**
 * Checkbox (#1135): a square box with a small radius, never a round one, so
 * it does not read as a radio button. Checked takes the accent fill and its
 * contrast-safe foreground.
 */
export const CHECKBOX_BOX = [
  'peer inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border',
  'border-[color-mix(in_srgb,var(--text-color)_45%,var(--window-background))] bg-[var(--window-background)]',
  'transition-[background-color,border-color]',
  CONTROL_MOTION,
  'hover:border-[var(--theme-interactive-color)]',
  'data-[state=checked]:border-[var(--theme-interactive-color)] data-[state=checked]:bg-[var(--theme-interactive-color)] data-[state=checked]:text-[var(--theme-interactive-foreground)]',
  'data-[state=indeterminate]:border-[var(--theme-interactive-color)] data-[state=indeterminate]:bg-[var(--theme-interactive-color)] data-[state=indeterminate]:text-[var(--theme-interactive-foreground)]',
  'disabled:cursor-not-allowed disabled:opacity-50',
].join(' ')

/**
 * Underlined tabs (#1521) for page-level navigation: the active tab carries an
 * accent underline instead of a filled pill. The list draws the baseline the
 * underline sits on.
 */
export const TABS_LIST_UNDERLINE =
  'inline-flex h-auto items-end justify-start gap-1 rounded-none border-b border-[var(--window-border-color)] bg-transparent p-0'

export const TABS_TRIGGER_UNDERLINE = [
  'inline-flex items-center gap-1.5 whitespace-nowrap -mb-px border-b-2 border-transparent px-3 py-2 text-sm font-medium',
  'text-[var(--text-secondary)] transition-colors',
  CONTROL_MOTION,
  'hover:text-[var(--text-color)]',
  'data-[state=active]:border-[var(--theme-interactive-color)] data-[state=active]:text-[var(--text-color)]',
  'disabled:pointer-events-none disabled:opacity-50',
].join(' ')

/** Small count next to a tab label. */
export const TABS_COUNT =
  'rounded-full bg-[color-mix(in_srgb,var(--text-color)_8%,transparent)] px-1.5 text-xs font-medium tabular-nums text-[var(--text-secondary)]'

/**
 * Status badges (#1521). The colour is the theme class's status colour; the
 * text is mixed halfway toward the theme's text colour so it reads on light
 * and dark backgrounds alike, the tint is the same colour at low strength.
 * Written out in full: Tailwind only generates classes it finds verbatim in
 * the source, so a class assembled at runtime gets no CSS.
 */
export const BADGE_STATUS_VARIANTS = {
  success:
    'border-transparent bg-[color-mix(in_srgb,var(--cdx-color-success)_14%,transparent)] text-[color-mix(in_srgb,var(--cdx-color-success)_50%,var(--text-color))]',
  warning:
    'border-transparent bg-[color-mix(in_srgb,var(--cdx-color-warning)_14%,transparent)] text-[color-mix(in_srgb,var(--cdx-color-warning)_50%,var(--text-color))]',
  danger:
    'border-transparent bg-[color-mix(in_srgb,var(--cdx-color-danger)_14%,transparent)] text-[color-mix(in_srgb,var(--cdx-color-danger)_50%,var(--text-color))]',
  info:
    'border-transparent bg-[color-mix(in_srgb,var(--status-updated)_14%,transparent)] text-[color-mix(in_srgb,var(--status-updated)_50%,var(--text-color))]',
  neutral:
    'border-transparent bg-[color-mix(in_srgb,var(--text-color)_8%,transparent)] text-[var(--text-secondary)]',
} as const

/**
 * Warning as plain text or icon colour. The theme's warning colour itself is
 * a light yellow that does not read on a light background.
 */
export const WARNING_TEXT = 'text-[color-mix(in_srgb,var(--cdx-color-warning)_50%,var(--text-color))]'

/** Leading dot in the badge's text colour. */
export const BADGE_DOT = "before:size-1.5 before:shrink-0 before:rounded-full before:bg-current before:content-['']"

/** Compact tables (#1521): small uppercase headers, tighter cells. */
export const TABLE_HEAD_COMPACT =
  'h-9 px-3 text-xs font-semibold uppercase tracking-wide text-[var(--text-secondary)]'

export const TABLE_CELL_COMPACT = 'px-3 py-2.5'
